import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema } from '../schemas';
import { baseStaticAnchorSchema } from './schemas';
import { AnchorTypes } from '@shared/constants/anchors.const';
import { SafetyViolationTypes } from '@shared/constants/safety.const';

// ============================================================
// Param Schemas
// ============================================================

const anchorParamsSchema = projectIdParamsSchema.extend({
	anchorId: requiredStringSchema.describe('Anchor identifier'),
});

const anchorPointParamsSchema = projectIdParamsSchema.extend({
	anchorPointId: requiredStringSchema.describe('Anchor point identifier'),
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * The array bodies drop their per-element `projectId`.
 *
 * That field was never a project key the guard could read — `collectRequestProjectIds`
 * looks at TOP-LEVEL `body.projectId`, and an array body has no top level — which is
 * why these routes carried a parallel `?projectId=` and a hand-written
 * `assertBodyProjectIdsMatchQuery` to reconcile the two. One value in the path
 * removes both the query param and the reconciliation.
 */
const insertAnchorPointBodySchema = z
	.array(
		z.object({
			areaId: requiredStringSchema,
			points: z.object({ x: z.number(), y: z.number() }),
		})
	)
	.min(1);

const insertStaticAnchorsBodySchema = z.array(baseStaticAnchorSchema.omit({ projectId: true }));

/**
 * `projectId` is GONE from the update body. It was declared
 * `z.string().optional()`, the handler merged it into `{ ...existingAnchor,
 * ...updateData }`, and the write used the QUERY's project in the filter and the
 * BODY's in the `$set` — a member of A could move an anchor into B.
 */
const updateAnchorBodySchema = z.object({
	areaId: z.string().optional(),
	anchorNick: z.string().optional(),
	anchorType: z.enum(AnchorTypes).optional(),
	safetyViolations: z.array(z.enum(SafetyViolationTypes)).optional(),
});

// ============================================================
// Contract
// ============================================================

/**
 * Static anchors + anchor points. `{ root: true }`, so every path is absolute.
 * Both collections key their project on the ordinary `projectId`.
 *
 * Two collections under one contract because they are edited together by the
 * backoffice floorplan: a point is placed first (`anchorId: null`) and an anchor
 * is created when the point is assigned one.
 */
export const staticAnchorsContract = defineContract(
	'staticAnchors',
	{
		// ── Anchors: collection ───────────────────────────────
		insertStaticAnchors: route({
			method: 'POST',
			path: '/projects/:projectId/anchors',
			params: projectIdParamsSchema,
			body: insertStaticAnchorsBodySchema,
			response: z.string(),
			operationId: 'insertStaticAnchors',
			tags: ['Static Anchors'],
			summary: 'Insert static anchors',
		}),
		getBaseAnchors: route({
			method: 'GET',
			path: '/projects/:projectId/anchors',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getBaseAnchors',
			tags: ['Static Anchors'],
			summary: 'Get base anchors by project',
		}),
		// The `{ids}`-body variant owns the collection DELETE; the delete-all twin
		// is the explicit `/all` sub-resource below. They are NOT the same request
		// with an empty list, and one of them is destructive by omission.
		deleteManyAnchors: route({
			method: 'DELETE',
			path: '/projects/:projectId/anchors',
			params: projectIdParamsSchema,
			body: z.object({ ids: z.array(z.string()) }),
			response: z.string(),
			operationId: 'deleteManyAnchors',
			tags: ['Static Anchors'],
			summary: 'Delete multiple anchors by IDs',
		}),

		// ── Anchors: sub-resources (static, before `/:anchorId`) ─
		getFullAnchors: route({
			method: 'GET',
			path: '/projects/:projectId/anchors/full',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getFullAnchors',
			tags: ['Static Anchors'],
			summary: 'Get full anchors with area details',
		}),
		assignAnchors: route({
			method: 'POST',
			path: '/projects/:projectId/anchors/assignments',
			params: projectIdParamsSchema,
			body: z.object({
				mappings: z
					.array(
						z.object({
							_id: requiredStringSchema,
							anchorId: requiredStringSchema,
						})
					)
					.min(1),
			}),
			response: z.any(),
			operationId: 'assignAnchors',
			tags: ['Static Anchors'],
			summary: 'Assign anchors to points',
		}),
		/**
		 * Overlaps `deleteAllAnchors` and is deliberately kept: this one nulls every
		 * anchor POINT's `anchorId` *and* deletes every static anchor, so the
		 * floorplan keeps its points and loses its anchors. `deleteAllAnchors`
		 * removes the anchors and their points. Both survive; consolidating them is
		 * a product decision, not a scope one.
		 */
		unassignAnchors: route({
			method: 'POST',
			path: '/projects/:projectId/anchors/unassignAll',
			params: projectIdParamsSchema,
			response: z.any(),
			operationId: 'unassignAnchors',
			tags: ['Static Anchors'],
			summary: 'Unassign every anchor point and delete the project’s anchors',
		}),
		deleteAllAnchors: route({
			method: 'DELETE',
			path: '/projects/:projectId/anchors/all',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'deleteAllAnchors',
			tags: ['Static Anchors'],
			summary: 'Delete all anchors (and their points) for a project',
		}),
		/** `?areaId=` is a data filter, not scope — the project is the path. */
		cleanAnchorCacheByAreaId: route({
			method: 'DELETE',
			path: '/projects/:projectId/anchors/cache',
			params: projectIdParamsSchema,
			query: z.object({ areaId: requiredStringSchema }),
			response: z.string(),
			operationId: 'cleanAnchorCacheByAreaId',
			tags: ['Static Anchors'],
			summary: 'Clean the anchor cache for one area of a project',
		}),

		// ── Anchors: items ────────────────────────────────────
		getAnchorByAnchorId: route({
			method: 'GET',
			path: '/projects/:projectId/anchors/:anchorId',
			params: anchorParamsSchema,
			response: z.any(),
			operationId: 'getAnchorByAnchorId',
			tags: ['Static Anchors'],
			summary: 'Get anchor by anchor ID',
		}),
		updateAnchor: route({
			method: 'PUT',
			path: '/projects/:projectId/anchors/:anchorId',
			params: anchorParamsSchema,
			body: updateAnchorBodySchema,
			response: z.string(),
			operationId: 'updateAnchor',
			tags: ['Static Anchors'],
			summary: 'Update an anchor',
		}),
		deleteAnchorByAnchorId: route({
			method: 'DELETE',
			path: '/projects/:projectId/anchors/:anchorId',
			params: anchorParamsSchema,
			response: z.string(),
			operationId: 'deleteAnchorByAnchorId',
			tags: ['Static Anchors'],
			summary: 'Delete anchor by anchor ID',
		}),

		// ── Anchor points ─────────────────────────────────────
		insertAnchorPoints: route({
			method: 'POST',
			path: '/projects/:projectId/anchorPoints',
			params: projectIdParamsSchema,
			body: insertAnchorPointBodySchema,
			response: z.any(),
			operationId: 'insertAnchorPoints',
			tags: ['Static Anchors'],
			summary: 'Insert anchor points',
		}),
		getAnchorPoints: route({
			method: 'GET',
			path: '/projects/:projectId/anchorPoints',
			params: projectIdParamsSchema,
			response: z.array(z.any()),
			operationId: 'getAnchorPoints',
			tags: ['Static Anchors'],
			summary: 'Get anchor points by project',
		}),
		updateAnchorPoint: route({
			method: 'PUT',
			path: '/projects/:projectId/anchorPoints/:anchorPointId',
			params: anchorPointParamsSchema,
			body: z.object({
				areaId: z.string().optional(),
				points: z.object({ x: z.number(), y: z.number() }).optional(),
			}),
			response: z.any(),
			operationId: 'updateAnchorPoint',
			tags: ['Static Anchors'],
			summary: 'Update an anchor point',
		}),
		deleteAnchorPoint: route({
			method: 'DELETE',
			path: '/projects/:projectId/anchorPoints/:anchorPointId',
			params: anchorPointParamsSchema,
			response: z.string(),
			operationId: 'deleteAnchorPoint',
			tags: ['Static Anchors'],
			summary: 'Delete an anchor point',
		}),
	},
	{ root: true }
);
